'use client'
import React, { useState, useEffect } from 'react';
import Image, { StaticImageData } from 'next/image';
import { architecturalheritage } from '@/lib/data';
import arc from "../../../public/arc1.png"
import yellowDotinactive from "../../../public/yellowDotinactive.png"
import yellowDotactive from "../../../public/yellowDotactive.svg"


import building2 from "../../../public/buildingphoto/Building2.png"
import building2a from "../../../public/buildingphoto/Building2A.png"
import building30 from "../../../public/buildingphoto/Building30.png"
import building8a from "../../../public/buildingphoto/Building8A.png"
import building24a from "../../../public/buildingphoto/Building24A.png"
import building28 from "../../../public/buildingphoto/Building28.png"
import building26 from "../../../public/buildingphoto/Building26.png"
import building21 from "../../../public/buildingphoto/Building21.png"

interface DotData {
  id: number;
  top: string;
  left: string;
  title: string;
  image: StaticImageData;
  content?: string;
    type?: string;
}
interface ModalProps {
  title: string;
  content: string;
  onClose: () => void;
  image: StaticImageData;
    type?: string;
}

const Modal = ({ title, content, onClose, image, type  }: ModalProps) => {
  // Handle Escape key press and prevent body scroll
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Handle scroll events on the modal to scroll the content area
  const handleModalScroll = (e: React.WheelEvent) => {
    e.preventDefault();
    const contentArea = e.currentTarget.querySelector('.content-area') as HTMLElement;
    if (contentArea) {
      const scrollAmount = e.deltaY;
      contentArea.scrollTop += scrollAmount;
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-[999999]"
      onClick={onClose}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 animate-fadeIn" style={{ animationDuration: '.5s', animationFillMode: 'forwards' }}></div>
      
      {/* Modal content with fade-in animation */}
      <div 
        className="bg-white w-full h-full md:w-[500px] md:max-h-[90vh] md:rounded-lg md:shadow-lg md:border md:border-gray-200 box-border relative overflow-hidden opacity-0 animate-fadeIn"
        style={{ animationDuration: '.5s', animationFillMode: 'forwards' }}
        onClick={(e) => e.stopPropagation()}
        onWheel={handleModalScroll}
      >
        <div className='h-32 md:h-40 w-full bg-[#CDE4E4] md:rounded-t-lg border-b-2' />
        <div className='p-4 md:p-[35px]'>
          <div className="md:mt-[-157px] mt-[-110px] relative">
            <button className="absolute top-0 right-0 cursor-pointer" onClick={onClose}>
              <Image src="/x-close.svg" alt="Close" width={24} height={24} />
            </button>
            <h2 id="intro-heading" className="text-2xl md:text-3xl font-bold text-[#005E60] mb-4 uppercase">
              {type === "operational-use" ? "Operational Use" : "Architectural Detail"}</h2>
            <div className="mb-9 border-[#CDE4E4] border-b-3">
              <Image src={image} alt={title} width={500} height={300} className="rounded mb-8 w-full h-auto" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-[#005E60] mb-2">{title}</h3>
            <div className="content-area text-xs md:text-sm text-[#212121] max-h-[14vh] overflow-y-auto pr-2" dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        </div>
      </div>
    </div>
  );
};

const dotsData = [
  { id: 1, top: '32.57%', left: '89.08%', title: 'Building 21', image: building21 },
  { id: 2, top: '28.70%', left: '73.37%', title: 'Building 26', image: building26 },
  { id: 3, top: '28.71%', left: '78.47%', title: 'Building 28', image: building28 },
  { id: 4, top: '33.18%', left: '71.65%', title: 'Building 24A', image: building24a },
  { id: 5, top: '42.64%', left: '27.61%', title: 'Building 8A', image: building8a },
  { id: 6, top: '32.58%', left: '13.05%', title: 'Building 30', image: building30 },
  { id: 7, top: '46.03%', left: '12.34%', title: 'Building 2A', image: building2a },
  { id: 8, top: '38.56%', left: '9.26%',  title: 'Building 2',  image: building2 },
];

const Heritage = () => {
  const [activeDot, setActiveDot] = useState<DotData | null>(null);

  const handleDotClick = (dot: DotData) => {
    const heritageData = architecturalheritage.find(
      (item) => item.title === dot.title
    );
    const content = heritageData?.content ?? "No content available";
    setActiveDot({
      ...dot,
      content: content as string,
      type: heritageData?.type,
    });
  };

  return (
    <section id="heritage" className="relative w-full md:min-h-screen bg-[#F4FBFB] px-4 pt-10 pb-4 md:px-12 flex flex-col md:flex-row md:items-center">
      <div className="container lg:mx-14 flex flex-col md:flex-row">
        <div className="max-w-xl text-[#005E60] space-y-6 md:pr-12">
          <h3 id="intro-heading"className="text-2xl md:text-4xl font-extrabold uppercase">Architectural Heritage & Continued Use</h3>
          <h2 className="text-3xl md:text-4xl font-medium leading-tight">
            Uncover Details About the<br />Buildings Being Retained
          </h2>
          <p className="text-black text-sm opacity-90">
            In accordance with the heritage designation approved by City Council in February 2026, GE Vernova will maintain four buildings currently in use and / or under GE Vernova’s control, all of which are located in the southeast portion of the site. <br /><br />
            Four additional buildings, located on the west portion of the site, are currently occupied and used by GE Vernova’s long-term tenant for industrial purposes. 
          </p>
          <button className="w-full md:w-auto bg-[#CDE4E4] text-[#005E60] px-4 py-3 rounded-md flex items-start gap-4 hover:bg-[#b5d9d9] transition font-medium">
            <Image src="/cursor.svg" alt="cursor icon" width={32} height={32} />
            <span className="text-left text-lg md:text-sm">
              Tap to uncover the details<br />of these buildings 
            </span>
          </button>
        </div>

        <div className="relative w-full mt-8 md:mt-0 md:ml-auto max-w-3xl">
          <div className="relative w-full aspect-[791/491]">
            <Image src={arc} alt="heritage map" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" className="object-contain w-full h-full" />
            {dotsData.map(dot => (
              <button
                key={dot.id}
                className="absolute z-10 cursor-pointer"
                style={{ top: dot.top, left: dot.left, transform: 'translate(-50%, -50%)' }}
                onClick={() => handleDotClick(dot)}
              >
                <Image 
                  src={activeDot?.id === dot.id ? yellowDotactive : yellowDotinactive} 
                  alt={`Dot ${dot.id}`} 
                  width={35} 
                  height={35} 
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeDot && (
        <Modal
          title={activeDot.title}
          content={activeDot.content || "No content available"}
          image={activeDot.image}
          type={activeDot.type}
          onClose={() => setActiveDot(null)}
        />
      )}
    </section>
  );
};

export default Heritage;
